import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { DataGrid, Switch, showNotif, hideNotif } from '@p81-common/ui';

import {
  FilterButton,
  ClearButton,
  SelectDropDown
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';

import { useDataGrid } from 'hooks/useDataGrid';
import { useContent } from 'contexts/content-context';
import { getRowHeight } from 'utils/common';

import classes from '../PlatformFeatureFlags.module.scss';
import createClasses from '../styles';
import {
  createFeatureFlagsConfig,
  defaultFilters
} from '../helpers/tableConfig';
import {
  assignFlags,
  errorReason,
  fetchFeatureFlags,
  fetchFilterOptions,
  GA_STATUS,
  unassignFlag
} from '../helpers/api';
import SelectionBar from './SelectionBar';
import AssignConfirmModal from './AssignConfirmModal';
import UnassignConfirmModal from './UnassignConfirmModal';
import FlagResultAlert from './FlagResultAlert';
import type {
  FeatureFlagRow,
  FeatureFlagsResponse,
  FilterOptions
} from '../helpers/types';

// Assigned and GA rows arrive already ticked and cannot be un-ticked; the row
// kebab is the only route to an unassign.
const isLocked = (row: FeatureFlagRow) =>
  row.state === 'assigned' || row.state === 'globalAssigned';

const FeatureFlagsTab = ({
  customerId,
  canWrite,
  region
}: {
  customerId: string;
  canWrite: boolean;
  region: string;
}) => {
  const styles = createClasses();
  const { t } = useTranslation(['activity']);
  // CustomerInfo already dispatches the tenant body into content-context, so
  // the name is available here without threading it through the routers.
  const { contentState } = useContent();
  const tenantName = contentState?.customerData?.companyName || 'this tenant';

  const [unassignTarget, setUnassignTarget] = useState<FeatureFlagRow | null>(
    null
  );
  const [unassigning, setUnassigning] = useState(false);

  const handleUnassign = (row: FeatureFlagRow) => setUnassignTarget(row);

  const tableConfig = useMemo(
    () => createFeatureFlagsConfig({ canWrite, onUnassign: handleUnassign }),
    [canWrite]
  );

  const {
    setFilter,
    filter,
    tableData,
    setTableData,
    tableConfigDisplay,
    numOfRowsOptions,
    handleChangeNumOfRows,
    handleTableSettingsMenuItemClick,
    tableSettingsDisplay,
    tableSettingsChanged,
    rowSelectionChanged,
    onSortEnd,
    loaderTable,
    setLoaderTable,
    filterButton,
    handleFilterButton
  } = useDataGrid(tableConfig, defaultFilters);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  // Single select, so this is a bare value rather than an array.
  const [owner, setOwner] = useState('');
  const [sort, setSort] = useState<
    { fieldName: string; direction: number } | undefined
  >();
  // Off by default: GA flags apply to every tenant, so they are noise when the
  // question is what THIS tenant holds. Off means they leave the result set
  // entirely, so the pager and the header agree with the rows on screen.
  const [includeGlobalAssigned, setIncludeGlobalAssigned] = useState(false);
  const [options, setOptions] = useState<FilterOptions>();
  const [flags, setFlags] = useState<FeatureFlagsResponse>();
  // Rows the user has opted in to assign, on top of the locked ones.
  const [picked, setPicked] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // Bumped after a commit to force a refetch without touching the filters.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchFilterOptions()
      .then(options => {
        !cancelled && setOptions(options);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoaderTable(true);
    fetchFeatureFlags(customerId, {
      currentPage: filter.currentPage,
      itemsPerPage: filter.itemsPerPage,
      search,
      status,
      owner,
      sort,
      includeGlobalAssigned
    })
      .then(body => {
        if (cancelled) return;
        setFlags(body);
        setTableData(body.data);
      })
      .catch(err => {
        if (cancelled) return;
        // Was a console.log behind an eslint-disable, which left the table
        // silently empty and indistinguishable from a tenant with no flags.
        showNotif(`Could not load feature flags: ${errorReason(err)}`, {
          alertProps: { severity: 'error' }
        });
      })
      .finally(() => {
        !cancelled && setLoaderTable(false);
      });
    // A staged count that outlived its rows would be misleading, so paging,
    // searching or filtering drops the selection.
    setPicked([]);
    setConfirmOpen(false);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerId,
    filter,
    search,
    status,
    owner,
    sort,
    includeGlobalAssigned,
    reloadKey
  ]);

  // Any change to what is being filtered goes back to page 1. Without this,
  // narrowing the results from page 3 lands on an empty table.
  const toPageOne = () => setFilter({ ...filter, currentPage: 1 });

  /*
   * With the toggle off there are no GA rows in the result set, so offering
   * "General Availability" as a Status is a dead choice: the server applies
   * the status match BEFORE dropping GA (ff.service.js, stateMatchStage then
   * globalAssignedStage), which leaves a guaranteed-empty table. Removing the
   * option is preferred over flipping the toggle automatically — silently
   * overriding a setting the user chose is worse than hiding a choice that
   * can only ever return nothing.
   */
  const statusOptions = useMemo(
    () =>
      includeGlobalAssigned
        ? options?.status
        : options?.status?.filter(option => option.value !== GA_STATUS),
    [options?.status, includeGlobalAssigned]
  );

  // Turning the toggle off while GA is selected would otherwise leave a
  // filter active that no longer has a control.
  const dropGaStatus = (next: string[]) =>
    next.filter(value => value !== GA_STATUS);

  const rows = (tableData || []) as unknown as FeatureFlagRow[];
  const lockedIds = rows.filter(isLocked).map(row => row._id);

  return (
    <>
      <div className={classes.filters_main}>
        <InputSearch
          placeholder="Search by feature name"
          value={search}
          onChange={(e: any) => {
            setSearch(e.target.value);
            toPageOne();
          }}
          onClearSearch={() => {
            setSearch('');
            toPageOne();
          }}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        {/* Wrapped rather than given a className: with `label` set, the DS
            Switch spreads the remaining props onto the inner switch and
            renders a MuiFormGroup around it, so a className on the component
            would style the control, not the flex item. */}
        <div className={styles.gaToggle}>
          <Switch
            label="Include GA Feature Flags"
            checked={includeGlobalAssigned}
            onChange={(_event, checked) => {
              setIncludeGlobalAssigned(checked);
              if (!checked) setStatus(dropGaStatus(status));
              toPageOne();
            }}
            disabled={loaderTable}
            row
          />
        </div>
      </div>

      <div
        className={cn(filterButton ? classes.filters : classes.filters_close)}
      >
        <SelectDropDown
          options={statusOptions}
          input={{
            value: status,
            onChange: (v: string[]) => {
              setStatus(v);
              toPageOne();
            }
          }}
          label="Status"
          multi
          // The open list renders `label`, but the collapsed control renders
          // `renderKey`, which defaults to 'value' — so the derived state keys
          // showed as "assigned" / "globalAssigned" once picked. Every other
          // filter panel passes 'label', including the global Feature Flag
          // Rollout page (FeatureList FiltersBlock.js:49).
          renderKey={'label'}
          // Without this the option list is seeded once and never updated
          // (SelectDropDown.js:65-76 latches on trigerGetOptions), so the GA
          // option would stay in the list after the toggle goes off.
          reload
        />
        <SelectDropDown
          options={options?.owner}
          input={{
            value: owner,
            onChange: (v: string) => {
              setOwner(v);
              toPageOne();
            }
          }}
          label="Owner"
        />
        <ClearButton
          onClick={() => {
            setStatus([]);
            setOwner('');
            toPageOne();
          }}
          numberОfFilters={2}
        />
      </div>

      <DataGrid
        getRowId={entry => entry._id}
        autoHeight
        lazyLoading
        loading={loaderTable}
        tableConfig={tableConfigDisplay}
        numOfRowsOptions={numOfRowsOptions}
        classes={{ container: styles.grid }}
        getRowHeight={getRowHeight}
        checkboxSelection
        // Locked rows stay ticked; without write access nothing is tickable.
        isRowSelectable={params =>
          canWrite && !isLocked(params.row as FeatureFlagRow)
        }
        selectionModel={[...lockedIds, ...picked]}
        onSelectionModelChange={model =>
          setPicked((model as string[]).filter(id => !lockedIds.includes(id)))
        }
        tableData={{
          page: flags?.page || 0,
          totalPages: flags?.totalPages || 0,
          totalRecords: flags?.totalRecords || 0,
          data: tableData || []
        }}
        paginationProps={{
          page: filter.currentPage - 1,
          count: flags?.totalRecords || 0,
          rowsPerPage: filter.itemsPerPage,
          onChangePage: (event, page) =>
            setFilter({ ...filter, currentPage: page + 1 }),
          hideNavigation: !flags?.totalRecords,
          hideRowCount: !flags?.totalRecords
        }}
        toolbarProps={{
          title1: 'Assigned Feature Flags:',
          // The rows rendered CHECKED in the current filter, not totalRecords.
          // With the GA toggle off — the default — no GA rows are in the set,
          // so this is the tenant's direct assignments EXCEPT any since
          // promoted to GA: rankExpression tests GA before direct membership,
          // so a flag still listed in customerIds but now GlobalAssigned
          // ranks as GA and leaves both the rows and this count. With the
          // toggle on, GA rows are present and ticked and are counted.
          title2: `(${flags?.assignedCount || 0})`,
          // Without onChangeNumOfRows the rows-per-page menu rendered its
          // options and did nothing, because only useDataGrid's handler moves
          // filter.itemsPerPage. Same omission hid the two tooltips.
          rowSelectionTooltipText: t(
            'activity:cloudManagementLogs.QUANTITY_OF_ITEMS'
          ),
          tableSettingsTooltipText: t(
            'activity:cloudManagementLogs.TABLE_SETTINGS'
          ),
          tableSettingsListItems: tableSettingsDisplay,
          rowSelectionChanged,
          tableSettingsChanged,
          handleTableSettingsMenuItemClick,
          onChangeNumOfRows: handleChangeNumOfRows,
          onSortEnd
        }}
        rowCount={flags?.totalRecords || 0}
        // sortingMode="server" with no handler meant the header did nothing.
        // Only name and ownerName are offered; see tableConfig for why Status
        // and Assigned On are not sortable.
        onSortModelChange={model => {
          const next = model[0];

          setSort(
            next?.field
              ? {
                  fieldName: next.field,
                  direction: next.sort === 'desc' ? -1 : 1
                }
              : undefined
          );
          toPageOne();
        }}
        sortingMode={'server'}
        paginationMode={'server'}
      />

      <SelectionBar
        count={picked.length}
        onCancel={() => setPicked([])}
        onAssign={() => setConfirmOpen(true)}
      />

      <AssignConfirmModal
        open={confirmOpen}
        region={region}
        isLoading={assigning}
        flags={rows.filter(row => picked.includes(row._id))}
        // Back is a step back, not a cancel: the selection survives.
        onBack={() => setConfirmOpen(false)}
        onApprove={async () => {
          setAssigning(true);
          const { succeeded, failed, reasons } = await assignFlags(
            picked,
            customerId
          );

          setAssigning(false);
          setConfirmOpen(false);
          setPicked([]);
          setReloadKey(key => key + 1);
          showNotif(
            <FlagResultAlert
              tenantName={tenantName}
              mode="Assigned"
              succeeded={succeeded}
              failed={failed}
              reasons={reasons}
            />,
            { ms: 10000, onBeforeClose: hideNotif }
          );
        }}
      />

      <UnassignConfirmModal
        flag={unassignTarget}
        tenantName={tenantName}
        isLoading={unassigning}
        onCancel={() => setUnassignTarget(null)}
        onConfirm={async () => {
          if (!unassignTarget) return;
          const flagName = unassignTarget.name;

          setUnassigning(true);
          let reason = '';
          try {
            await unassignFlag(unassignTarget._id, customerId);
          } catch (err) {
            // The GA guard answers 409 with the reason; discarding it left the
            // user with a bare "hasn't been Unassigned".
            reason = errorReason(err);
          }
          setUnassigning(false);
          setUnassignTarget(null);
          setReloadKey(key => key + 1);
          showNotif(
            <FlagResultAlert
              tenantName={tenantName}
              mode="Unassigned"
              succeeded={reason ? 0 : 1}
              failed={reason ? 1 : 0}
              flagName={flagName}
              reasons={reason ? [reason] : []}
            />,
            { ms: 10000, onBeforeClose: hideNotif }
          );
        }}
      />
    </>
  );
};

export default FeatureFlagsTab;
